// ─────────────────────────────────────────────────────────────────
// Glass-i — RFQ list (Stage 6RFQ). A compact procurement worklist: one
// filter bar that doubles as the pipeline count, then a dense grid that
// says what each RFQ needs next — chase a quote, award the winner, or
// it's overdue. New RFQs are raised from an APPROVED Purchase Request
// (see "Create RFQ" on the PR page).
// ─────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button, Card, Empty, Input, Progress, Space, Table, Tag, Tooltip, message
} from 'antd';
import {
  ReloadOutlined, SearchOutlined, SolutionOutlined, RightOutlined,
  WarningOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { rfqApi } from './rfqApi';
import { rfqStatusLabel, responsePct } from './rfqHelpers';
import './RfqListPage.css';

const LIVE = ['DRAFT', 'SENT', 'PARTIALLY_RESPONDED', 'RESPONSES_RECEIVED'];

// Filter tabs — `key` drives the status-tinted count badge so the tab row
// mirrors the colour language of the Status column.
const TABS = [
  { label: 'All', value: '', key: 'all' },
  { label: 'Draft', value: 'DRAFT', key: 'draft' },
  { label: 'Sent', value: 'SENT', key: 'sent' },
  { label: 'Partial', value: 'PARTIALLY_RESPONDED', key: 'partial' },
  { label: 'Ready', value: 'RESPONSES_RECEIVED', key: 'ready' },
  { label: 'Converted', value: 'CONVERTED', key: 'converted' },
  { label: 'Cancelled', value: 'CANCELLED', key: 'cancelled' }
];

// What the buyer should do next — derived from status + how many vendors
// have come back. Tone maps to the row's intent (chase / act / done).
function nextStep(r) {
  const total = r.vendorCount || 0;
  const done = r.respondedCount || 0;
  switch (r.status) {
    case 'DRAFT': return total ? { t: 'Send to vendors', tone: 'blue' } : { t: 'Add vendors', tone: 'blue' };
    case 'SENT': return { t: done ? `Awaiting ${total - done} more` : 'Awaiting quotes', tone: 'mute' };
    case 'PARTIALLY_RESPONDED': return { t: `Awaiting ${total - done} more`, tone: 'mute' };
    case 'RESPONSES_RECEIVED': return { t: 'Ready to award', tone: 'green' };
    case 'CONVERTED': return { t: 'PO created', tone: 'mute' };
    case 'CANCELLED': return { t: 'Closed', tone: 'mute' };
    default: return null;
  }
}

// Status dot colours — a calm signal that doesn't wash the whole cell the
// way a filled tag does. Mirrors the filter-tab badge tints.
const STATUS_DOT = {
  DRAFT: '#94a3b8', SENT: '#1877f2', PARTIALLY_RESPONDED: '#f59e0b',
  RESPONSES_RECEIVED: '#06b6d4', CONVERTED: '#16a34a', CANCELLED: '#dc2626'
};

// Days to the submission deadline, only while the RFQ is still live.
function dueDays(r) {
  if (!r.submissionDueDate || !LIVE.includes(r.status)) return null;
  return dayjs(r.submissionDueDate).startOf('day').diff(dayjs().startOf('day'), 'day');
}

export default function RfqListPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [q, setQ] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await rfqApi.list({ pageSize: 200 });
      setRows(Array.isArray(res) ? res : (res.rows || []));
    } catch (e) {
      message.error(e?.response?.data?.error?.message || e.message || 'Failed to load RFQs.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Per-status counts feed the filter badges; overdue is a cross-status
  // condition surfaced separately only when it actually happens.
  const counts = useMemo(() => {
    const c = { all: rows.length, byStatus: {}, overdue: 0 };
    rows.forEach((r) => {
      c.byStatus[r.status] = (c.byStatus[r.status] || 0) + 1;
      const d = dueDays(r);
      if (d != null && d < 0) c.overdue += 1;
    });
    return c;
  }, [rows]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (status && r.status !== status) return false;
      if (overdueOnly) { const d = dueDays(r); if (!(d != null && d < 0)) return false; }
      if (!needle) return true;
      return `${r.rfqNo || ''} ${r.prNoSnapshot || ''}`.toLowerCase().includes(needle);
    });
  }, [rows, status, overdueOnly, q]);

  // Each tab carries its own count — the filter row IS the pipeline view.
  const tabCount = (v) => (v === '' ? counts.all : (counts.byStatus[v] || 0));

  const columns = useMemo(() => [
    { title: 'RFQ', dataIndex: 'rfqNo',
      render: (v, r) => (
        <div className="rfq-cell-id">
          <span className="rfq-cell-id__no">{v}</span>
          <div className="rfq-cell-sub">
            {r.prNoSnapshot ? r.prNoSnapshot : 'no source PR'}
            {r.lineCount ? ` · ${r.lineCount} item${r.lineCount === 1 ? '' : 's'}` : ''}
          </div>
        </div>) },
    { title: 'Raised', dataIndex: 'rfqDate', width: 122,
      sorter: (a, b) => dayjs(a.rfqDate).valueOf() - dayjs(b.rfqDate).valueOf(),
      defaultSortOrder: 'descend',
      render: (v) => v ? <span className="rfq-date">{dayjs(v).format('DD MMM YYYY')}</span> : '—' },
    { title: 'Responses', dataIndex: 'vendorCount', width: 168,
      render: (v, r) => {
        const total = v ?? 0;
        const done = r.respondedCount ?? 0;
        if (!total) return <span className="rfq-cell-sub">no vendors</span>;
        const pct = responsePct(done, total);
        return (
          <div className="rfq-cell-progress">
            <Progress percent={pct} size="small" showInfo={false}
              strokeColor={pct === 100 ? '#16a34a' : '#1877f2'} trailColor="#eef2f7" />
            <span className="rfq-cell-progress__txt">{done}/{total}</span>
          </div>); } },
    { title: 'Due', dataIndex: 'submissionDueDate', width: 116,
      render: (v, r) => {
        const d = dueDays(r);
        if (d == null) return <span className="rfq-cell-sub">{v ? dayjs(v).format('DD MMM') : '—'}</span>;
        const color = d < 0 ? 'red' : d <= 2 ? 'gold' : 'default';
        const label = d < 0 ? `${Math.abs(d)}d over` : d === 0 ? 'today' : `${d}d left`;
        return (
          <Tooltip title={`Due ${dayjs(v).format('DD MMM YYYY')}`}>
            <Tag color={color} className="rfq-due-tag">{label}</Tag>
          </Tooltip>); } },
    { title: 'Status', dataIndex: 'status', width: 200,
      render: (v, r) => {
        const ns = nextStep(r);
        return (
          <div className="rfq-cell-status">
            <span className="rfq-sdot" style={{ background: STATUS_DOT[v] || '#94a3b8' }} />
            <span className="rfq-sname">{rfqStatusLabel(v)}</span>
            {ns && <span className={`rfq-next rfq-next--${ns.tone}`}>{ns.t}</span>}
          </div>); } },
    { title: '', key: '_a', width: 36, align: 'right',
      render: () => <RightOutlined className="rfq-go" /> }
  ], []);

  const rowClassName = (r) => {
    if (r.status === 'RESPONSES_RECEIVED') return 'rfq-row-ready';
    const d = dueDays(r);
    if (d != null && d < 0) return 'rfq-row-overdue';
    return '';
  };

  return (
    <div className="rfq-list" style={{ padding: '12px 14px' }}>
      <div className="rfq-page-head">
        <div className="rfq-page-head__title">
          <SolutionOutlined className="rfq-page-head__icon" />
          <div>
            <h2>Requests for Quotation</h2>
            <p>Raise an RFQ from an approved request, send it to vendors, compare quotes, then award the best.</p>
          </div>
        </div>
        <Space>
          <Input allowClear prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
            placeholder="Search RFQ or PR no…" value={q} onChange={(e) => setQ(e.target.value)}
            style={{ width: 220 }} />
          <Tooltip title="Reload"><Button icon={<ReloadOutlined />} loading={loading} onClick={load} /></Tooltip>
        </Space>
      </div>

      <Card bordered={false} className="shadow-card" styles={{ body: { padding: 0 } }}>
        <div className="rfq-list-toolbar">
          <div className="rfq-tabs" role="tablist" aria-label="Filter RFQs by status">
            {TABS.map((t) => {
              const n = tabCount(t.value);
              const active = status === t.value && !overdueOnly;
              return (
                <button key={t.value || 'all'} type="button" role="tab" aria-selected={active}
                  className={`rfq-tab rfq-tab--${t.key}${active ? ' is-active' : ''}${n === 0 && !active ? ' is-empty' : ''}`}
                  onClick={() => { setStatus(t.value); setOverdueOnly(false); }}>
                  {t.label}<span className="rfq-tab__n">{n}</span>
                </button>);
            })}
          </div>
          <div className="rfq-list-toolbar__right">
            {counts.overdue > 0 && (
              <button type="button"
                className={`rfq-overdue-flag${overdueOnly ? ' is-on' : ''}`}
                onClick={() => { setOverdueOnly((s) => !s); setStatus(''); }}>
                <WarningOutlined /> {counts.overdue} overdue
              </button>)}
            <span className="rfq-cell-sub">{filtered.length} of {rows.length}</span>
          </div>
        </div>
        <Table
          className="rfq-grid"
          size="small"
          rowKey="id"
          loading={loading}
          dataSource={filtered}
          columns={columns}
          rowClassName={rowClassName}
          onRow={(r) => ({ onClick: () => navigate(`/erp/rfq/${r.id}`), style: { cursor: 'pointer' } })}
          pagination={{ pageSize: 25, hideOnSinglePage: true, showSizeChanger: false }}
          locale={{ emptyText: (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={<span style={{ color: '#64748b' }}>
                {overdueOnly ? 'Nothing overdue — every live RFQ is on time.'
                  : 'No RFQs here yet. Open an approved Purchase Request and click '}
                {!overdueOnly && <b>Create RFQ</b>}{!overdueOnly && '.'}</span>} />
          ) }}
        />
      </Card>
    </div>
  );
}
